'use client';

import { useState, useEffect } from 'react';
import { Grid, List, Filter, Heart } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import Link from 'next/link';

export default function PropertyListHeader({
  title,
  description,
  totalProperties,
  isFilterOpen,
  setIsFilterOpen,
  viewMode,
  setViewMode,
}) {
  const [savedCount, setSavedCount] = useState(0);

  const loadCount = () => {
    try {
      const resale = JSON.parse(
        localStorage.getItem('xspace_saved_properties') || '[]'
      );
      const newProject = JSON.parse(
        localStorage.getItem('xspace_saved_properties_new_project') || '[]'
      );

      // ✅ Total count from both
      setSavedCount(resale.length + newProject.length);
    } catch (error) {
      console.error('Error loading saved properties count:', error);
      setSavedCount(0);
    }
  };

  useEffect(() => {
    loadCount(); // Initial load

    const handler = () => loadCount();

    // Listen for both events
    window.addEventListener('xspace_saved_updated', handler); // resale legacy
    window.addEventListener('xspace_saved_properties_new_project', handler);

    return () => {
      window.removeEventListener('xspace_saved_updated', handler);
      window.removeEventListener(
        'xspace_saved_properties_new_project',
        handler
      );
    };
  }, []);

  return (
    <div className='bg-white dark:bg-gray-800 rounded-xl shadow-lg p-6 mb-8'>
      <div className='flex flex-col md:flex-row md:items-center md:justify-between gap-4'>
        <div>
          <h2 className='text-3xl font-bold text-gray-900 dark:text-white'>
            {title}
          </h2>
          <p className='text-gray-600 dark:text-gray-400 mt-2 max-w-2xl'>
            {description}
          </p>
          <p className='text-primarycolor dark:text-secondarycolor font-medium mt-2'>
            {totalProperties}{' '}
            {totalProperties === 1 ? 'property' : 'properties'} found
          </p>
        </div>

        <div className='flex items-center gap-4'>
          {/* Saved Properties Button */}
          <Button
            aschild='true'
            variant='outline'
            size='sm'
            className='relative hover:bg-secondarycolor dark:hover:bg-secondarycolor dark:hover:text-black'
          >
            <Link
              rel='preload'
              href='/saved-properties'
              className='flex items-center gap-1'
            >
              <Heart size={16} className='text-red-500 fill-current' />
              <span>Saved</span>
              {savedCount > 0 && (
                <Badge className='ml-1 bg-red-500 hover:bg-red-600 text-white text-xs px-1.5 py-0.5 min-w-[20px] h-5'>
                  {savedCount}
                </Badge>
              )}
            </Link>
          </Button>

          <button
            onClick={() => setIsFilterOpen(!isFilterOpen)}
            className='flex items-center gap-2 px-4 py-2 bg-gray-100 dark:bg-gray-700 rounded-lg text-gray-700 dark:text-gray-300 hover:bg-gray-200 dark:hover:bg-gray-600 lg:hidden'
          >
            <Filter size={18} />
            <span>Filters</span>
          </button>

          <div className='hidden md:flex items-center gap-2 bg-gray-100 dark:bg-gray-700 rounded-lg p-1'>
            <button
              onClick={() => setViewMode('grid')}
              className={`p-2 rounded-md ${
                viewMode === 'grid'
                  ? 'bg-white dark:bg-gray-800 text-primarycolor dark:text-secondarycolor shadow-sm'
                  : 'text-gray-500 dark:text-gray-400 hover:bg-gray-200 dark:hover:bg-gray-600'
              }`}
              aria-label='Grid view'
            >
              <Grid size={18} />
            </button>
            <button
              onClick={() => setViewMode('list')}
              className={`p-2 rounded-md ${
                viewMode === 'list'
                  ? 'bg-white dark:bg-gray-800 text-primarycolor dark:text-secondarycolor shadow-sm'
                  : 'text-gray-500 dark:text-gray-400 hover:bg-gray-200 dark:hover:bg-gray-600'
              }`}
              aria-label='List view'
            >
              <List size={18} />
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}
